import {
  DATASET_ERROR_CODES,
  DATASET_PREVIEW_LIMITS,
  type Dataset,
  type DatasetPreviewLimit,
} from '@contracts/datasets'
import { isApiError } from '@/shared/api'

/** URL state of the Data Preview route. The default limit is never written to the address. */
export interface DatasetPreviewSearch {
  limit?: DatasetPreviewLimit
}

function isPreviewLimit(value: unknown): value is DatasetPreviewLimit {
  return (DATASET_PREVIEW_LIMITS as readonly unknown[]).includes(value)
}

/** Tolerant parser: any other limit is read as the default instead of failing the route. */
export function parsePreviewSearch(search: Record<string, unknown>): DatasetPreviewSearch {
  const { limit } = search
  return { limit: isPreviewLimit(limit) && limit !== DATASET_PREVIEW_LIMITS[0] ? limit : undefined }
}

export type DatasetPageState =
  | 'loading'
  /** Unknown, deleted, or an address whose identifier is malformed. */
  | 'not-found'
  /** In progress or in error: nothing to preview. */
  | 'not-completed'
  | 'failed'
  | 'ready'

/** What the page shows, from the request of the dataset's details. */
export function datasetPageState(details: {
  data: Dataset | undefined
  error: unknown
}): DatasetPageState {
  if (details.data !== undefined) {
    return details.data.status === 'completed' ? 'ready' : 'not-completed'
  }
  if (details.error === null || details.error === undefined) return 'loading'
  const { error } = details
  if (isApiError(error)) {
    if (error.code === DATASET_ERROR_CODES.notFound || error.status === 404) return 'not-found'
    if (error.status === 422) return 'not-found'
  }
  return 'failed'
}

export type PreviewRefusal = 'file-missing' | 'file-unsupported' | 'storage-unavailable' | 'failed'

/** Why the backend returned no preview; selects the message of the Dataset Preview section. */
export function previewRefusal(error: unknown): PreviewRefusal {
  if (!isApiError(error)) return 'failed'
  if (error.code === DATASET_ERROR_CODES.fileMissing) return 'file-missing'
  if (error.code === DATASET_ERROR_CODES.fileUnsupported) return 'file-unsupported'
  if (error.code === DATASET_ERROR_CODES.storageUnavailable) return 'storage-unavailable'
  return 'failed'
}
